import React, { useRef, useState, useContext, useEffect } from 'react';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import CommonApi from '../CommonApi';
import { Tokens } from 'ordercloud-javascript-sdk';
import { EnvironmentConfig } from '../../environments/environmentconfig';
import { useTranslation } from 'react-i18next';
import Cookies from 'js-cookie';

export const UploadHistoryWidget = () => {
  const [files, setFiles] = useState([]);
  const [className, setClassname] = useState(
    'surface-0 shadow-2 p-3 border-1 border-50 border-round height280'
  );
  const toast = useRef(null);
  const commonCtx = useContext(CommonApi);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!commonCtx.loading && loading) {
      if (!commonCtx.isLoggedIn) {
        location.assign('/' + Cookies.get('languageID') + '/');
      } else {
        commonCtx.listAddresses();
        loadUploadHistory();
        setLoading(false);
      }
    }

    if (location.pathname === '/' + Cookies.get('languageID') + '/upload') {
      document.title = commonCtx.title + ` - ${t('UploadHistory')}`;
      setClassname('surface-0 shadow-2 p-3 border-1 border-50 border-round heightTotal my-3');

      // Listener per il custom event di upload completato
      const handleUploadCompleted = (event) => {
        console.log('UploadHistoryWidget: ricevuto evento upload completato');
        loadUploadHistory(); // Ricarica i dati
      };

      // Aggiungi listener al window solo nella pagina upload
      window.addEventListener('uploadCompleted', handleUploadCompleted);

      // Cleanup quando il componente si smonta o cambia pagina
      return () => {
        window.removeEventListener('uploadCompleted', handleUploadCompleted);
      };
    }
  });

  const loadUploadHistory = async () => {
    try {
      const accessToken = Tokens.GetAccessToken();

      const response = await fetch(`${EnvironmentConfig.apiMiddlewareUrl}api/UploadHistory`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Trasforma i dati dal formato API al formato richiesto dalla tabella
      const transformedFiles =
        data.Files?.map((file) => ({
          fileId: file.FileId,
          fileName: file.FileName,
          date: file.Date,
          status: getStatusText(file.Status),
          originalStatus: file.Status,
          rowsCount: file.Rows?.length || 0,
          successCount: file.Rows?.filter((row) => row.Status === 'Success').length || 0,
          errorCount: file.Rows?.filter((row) => row.Status === 'Error').length || 0,
        })) || [];

      setFiles(transformedFiles);
    } catch (error) {
      console.error('Errore nel caricamento dello storico:', error);
      /* toast.current?.show({
        severity: 'error',
        summary: t('Error'),
        detail: t('ErrorLoadingHistory'),
      }); */
      setFiles([]); // Set empty array in case of error
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'Processing':
        return t('Processing');
      case 'Completed':
        return t('UploadCompleted');
      case 'CompletedWithErrors':
        return t('CompletedWithErrors');
      default:
        return status;
    }
  };

  const handleDownload = async (file) => {
    try {
      const accessToken = Tokens.GetAccessToken();

      const response = await fetch(
        `${EnvironmentConfig.apiMiddlewareUrl}api/UploadHistory/export/${file.fileId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Ottieni il nome del file dall'header Content-Disposition se presente
      const contentDisposition = response.headers.get('Content-Disposition');
      let fileName = `upload_results_${file.fileId}_${new Date().toISOString().slice(0, 10)}.xlsx`;

      if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
        if (fileNameMatch) {
          fileName = fileNameMatch[1];
        }
      }

      // Converti la risposta in blob
      const blob = await response.blob();

      // Crea un link temporaneo per il download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      // Pulizia
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      toast.current?.show({
        severity: 'success',
        summary: t('Success'),
        detail: t('FileDownloadedSuccessfully'),
      });
    } catch (error) {
      console.error('Errore nel download del file:', error);
      toast.current?.show({
        severity: 'error',
        summary: t('Error'),
        detail: t('ErrorDownloadingFile'),
      });
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const dateBodyTemplate = (rowData) => {
    return formatDate(rowData.date);
  };

  const statusBodyTemplate = (rowData) => {
    let severity = 'info';
    let icon = 'pi pi-clock';

    switch (rowData.originalStatus) {
      case 'Processing':
        severity = 'info';
        icon = 'pi pi-spin pi-spinner';
        break;
      case 'Completed':
        severity = 'success';
        icon = 'pi pi-check';
        break;
      case 'CompletedWithErrors':
        severity = 'warning';
        icon = 'pi pi-exclamation-triangle';
        break;
      default:
        severity = 'info';
        icon = 'pi pi-clock';
    }

    return (
      <div className="flex align-items-center gap-2">
        <i className={`${icon} text-${severity}`} />
        <span className={`badge badge-${severity}`}>{rowData.status}</span>
        {rowData.originalStatus === 'CompletedWithErrors' && (
          <small className="text-muted">
            ({rowData.successCount} {t('Ok')}, {rowData.errorCount} {t('Errors')})
          </small>
        )}
      </div>
    );
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <Button
        icon="pi pi-download"
        className="p-button-rounded p-button-text"
        onClick={() => handleDownload(rowData)}
        tooltip={t('Download')}
      />
    );
  };

  return (
    <div className={className}>
      <div className="flex justify-content-between mb-3">
        <div className="surface-0 w-full h-auto">
          <div className="flex justify-content-between mb-3">
            <Toast ref={toast}></Toast>

            <div className="w-full">
              <div className="text-xl text-900 font-medium mb-3">{t('UploadHistory')}</div>

              <DataTable
                value={files}
                loading={loading}
                emptyMessage={t('NoFilesFound')}
                responsiveLayout="scroll"
                className="p-datatable-sm"
              >
                <Column field="fileName" header={t('FileName')} sortable />
                <Column field="date" header={t('Date')} body={dateBodyTemplate} sortable />
                <Column field="status" header={t('Status')} body={statusBodyTemplate} sortable />
                <Column
                  header={t('Download')}
                  body={actionBodyTemplate}
                  exportable={false}
                  style={{ minWidth: '100px' }}
                />
              </DataTable>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default UploadHistoryWidget;
